import { useState, type ReactNode } from 'react';
import { DirectoryCard, DirectoryRow } from './DirectoryCard';
import { ViewToggle } from './ViewToggle';
import type { DirectoryEntry, DirectoryViewMode } from './types';

const STORAGE_KEY = 'dsr.directory.view';

function readStoredMode(): DirectoryViewMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'list' ? 'list' : 'cards';
  } catch {
    return 'cards';
  }
}

// Shared Directory page body: header + optional toolbar, a filters row with the
// Cards/List toggle on the right, and the entries rendered as a card grid or a
// list. The view mode is remembered across pages and reloads (localStorage).
export function DirectoryView({
  title,
  subtitle,
  actions,
  filters,
  entries,
  isLoading,
  isError,
  errorText = 'Failed to load.',
  emptyText = 'Nothing matches the current filters.',
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  filters?: ReactNode;
  entries: DirectoryEntry[];
  isLoading: boolean;
  isError: boolean;
  errorText?: string;
  emptyText?: string;
}): JSX.Element {
  const [mode, setMode] = useState<DirectoryViewMode>(readStoredMode);

  const changeMode = (m: DirectoryViewMode) => {
    setMode(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore persistence failures (private mode, quota) */
    }
  };

  return (
    <div className="page directory-page">
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p className="page-sub">{subtitle}</p>}
        </div>
        {actions && <div className="page-toolbar">{actions}</div>}
      </header>

      <div className="directory-controls">
        <div className="directory-filters">{filters}</div>
        <ViewToggle value={mode} onChange={changeMode} />
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && (
        <p className="error-text" role="alert">
          {errorText}
        </p>
      )}
      {!isLoading && !isError && entries.length === 0 && (
        <p className="muted">{emptyText}</p>
      )}

      {!isLoading &&
        !isError &&
        entries.length > 0 &&
        (mode === 'cards' ? (
          <div className="dir-grid">
            {entries.map((e) => (
              <DirectoryCard key={e.id} entry={e} />
            ))}
          </div>
        ) : (
          <div className="dir-list">
            {entries.map((e) => (
              <DirectoryRow key={e.id} entry={e} />
            ))}
          </div>
        ))}
    </div>
  );
}
